import { z } from "zod";
import { colorSchema, dateOnlySchema, listQuerySchema, tagsSchema, timestampSchema, uuidSchema } from "./api.ts";
import { clientRefSchema } from "./clients.ts";
import { blankToNull, csvArraySchema, queryBooleanSchema, timestampsShape, userRefSchema } from "./common.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";

/**
 * CONTRACT of the `projects` module — PRD §7 Item 3 (the project half; tasks
 * ship next against the same public API).
 *
 * Vocabulary: a **Project** delivers work for a **Client** (required unless the
 * project is `internal`) and optionally against one **Engagement** (the
 * commercial arrangement). Retainer commitments live on the engagement as
 * **deliverable quotas**, and every delivered item is a row in the
 * **deliverable ledger** — so planned / in progress / delivered can be metered
 * against what was committed, per month, with rollover.
 *
 * Money (`budgetMinor`) is masked to `null` for callers without
 * `financials:view`, exactly as the catalog masks prices.
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** Retainer = ongoing engagement work, one-off = fixed scope, internal = no client (PRD US-3.1). */
export const PROJECT_TYPES = ["retainer", "one_off", "internal"] as const;
export type ProjectType = (typeof PROJECT_TYPES)[number];
export const projectTypeSchema = z.enum(PROJECT_TYPES);

export const PROJECT_STATUSES = ["not_started", "active", "on_hold", "completed", "cancelled"] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
export const projectStatusSchema = z.enum(PROJECT_STATUSES);

/** The PM's weekly signal (PRD US-3.6). Also set by `POST /projects/:id/status-update`. */
export const PROJECT_HEALTHS = ["on_track", "at_risk", "off_track"] as const;
export type ProjectHealth = (typeof PROJECT_HEALTHS)[number];
export const projectHealthSchema = z.enum(PROJECT_HEALTHS);

export const MILESTONE_STATUSES = ["pending", "in_progress", "done", "cancelled"] as const;
export type MilestoneStatus = (typeof MILESTONE_STATUSES)[number];
export const milestoneStatusSchema = z.enum(MILESTONE_STATUSES);

/** Unused committed items either expire with the period or carry forward, capped (PRD US-3.5). */
export const ROLLOVER_POLICIES = ["none", "carry_forward"] as const;
export type RolloverPolicy = (typeof ROLLOVER_POLICIES)[number];
export const rolloverPolicySchema = z.enum(ROLLOVER_POLICIES);

/** Where a ledger item is in its life: only `delivered` counts against the quota. */
export const DELIVERABLE_STATES = ["planned", "in_progress", "delivered"] as const;
export type DeliverableState = (typeof DELIVERABLE_STATES)[number];
export const deliverableStateSchema = z.enum(DELIVERABLE_STATES);

/** Phase 1 meters a calendar month; the column exists so quarters can follow without a migration. */
export const QUOTA_PERIOD_UNITS = ["month"] as const;
export type QuotaPeriodUnit = (typeof QUOTA_PERIOD_UNITS)[number];
export const quotaPeriodUnitSchema = z.enum(QUOTA_PERIOD_UNITS);

/** Suggestions for the deliverable-type picker; the column is free text so an agency can add its own. */
export const DEFAULT_DELIVERABLE_TYPES = [
  "static_post",
  "carousel",
  "reel",
  "story",
  "blog",
  "video",
  "report",
  "email_campaign",
  "landing_page",
  "creative_set",
] as const;

/** `P-0123` — the project number format of US-3.1, gapless per workspace. */
export const PROJECT_NUMBER_REGEX = /^P-\d{4,}$/;

/** A quota period key, "YYYY-MM". */
export const PERIOD_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;
export const periodSchema = z.string().regex(PERIOD_REGEX, "Expected a period like 2026-04");

/** An ISO week key, "2026-W38" — the bucket the Friday status-update reminder counts in. */
export const WEEK_KEY_REGEX = /^\d{4}-W\d{2}$/;
export const weekKeySchema = z.string().regex(WEEK_KEY_REGEX, "Expected a week key like 2026-W38");

/** "At risk": ≥80% of the period elapsed with <60% of the commitment delivered (PRD US-3.5). */
export const QUOTA_AT_RISK_ELAPSED_BPS = 8000;
export const QUOTA_AT_RISK_DELIVERED_BPS = 6000;

// ---------------------------------------------------------------------------
// Period helpers (pure — the API and the web compute the same buckets)
// ---------------------------------------------------------------------------

/** The "YYYY-MM" bucket a moment falls in (UTC, so a period never depends on who is looking). */
export function periodOf(at: Date = new Date()): string {
  return `${at.getUTCFullYear()}-${String(at.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** [start, end) of a period in UTC. */
export function periodBounds(period: string): { start: Date; end: Date } {
  const [year, month] = period.split("-").map(Number) as [number, number];
  return { start: new Date(Date.UTC(year, month - 1, 1)), end: new Date(Date.UTC(year, month, 1)) };
}

/** Shifts a period by whole months ("2026-01" + -1 → "2025-12"). */
export function addPeriods(period: string, months: number): string {
  const [year, month] = period.split("-").map(Number) as [number, number];
  const shifted = new Date(Date.UTC(year, month - 1 + months, 1));
  return periodOf(shifted);
}

/** How far through the period we are, in basis points (0 before it starts, 10000 once it is over). */
export function periodElapsedBps(period: string, at: Date = new Date()): number {
  const { start, end } = periodBounds(period);
  if (at <= start) return 0;
  if (at >= end) return 10000;
  return Math.round(((at.getTime() - start.getTime()) / (end.getTime() - start.getTime())) * 10000);
}

/** ISO-8601 week key of a moment ("2026-W38") — Monday starts the week. */
export function isoWeekKey(at: Date = new Date()): string {
  const d = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/** Monday 00:00 UTC of the ISO week a moment falls in. */
export function startOfIsoWeek(at: Date = new Date()): Date {
  const d = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - (day - 1));
  return d;
}

// ---------------------------------------------------------------------------
// Project DTOs
// ---------------------------------------------------------------------------

/** One person on the project, denormalized onto the project row for cheap list rendering. */
export const projectTeamMemberSchema = z.object({
  userId: uuidSchema,
  user: userRefSchema.nullable(),
  roleInProject: z.string().nullable(),
  allocationPercent: z.number().int().nullable(),
});
export type ProjectTeamMember = z.infer<typeof projectTeamMemberSchema>;

/** Compact project reference other modules (tasks, invoices, time) embed in their payloads. */
export const projectRefSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  name: z.string(),
  status: projectStatusSchema,
  clientId: uuidSchema.nullable(),
});
export type ProjectRef = z.infer<typeof projectRefSchema>;

/** A project. `clientId` is required unless `type = "internal"` (PRD §4 Projects). */
export const projectSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  name: z.string(),
  clientId: uuidSchema.nullable(),
  client: clientRefSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  type: projectTypeSchema,
  status: projectStatusSchema,
  health: projectHealthSchema,
  healthNote: z.string().nullable(),
  healthSetAt: timestampSchema.nullable(),
  healthSetBy: userRefSchema.nullable(),
  pmId: uuidSchema.nullable(),
  pm: userRefSchema.nullable(),
  team: z.array(projectTeamMemberSchema),
  startDate: dateOnlySchema.nullable(),
  endDate: dateOnlySchema.nullable(),
  description: z.string().nullable(),
  brief: z.string().nullable(),
  /** `null` without `financials:view` (Phase 2 deepens budgets into burn-down). */
  budgetMinor: minorSchema.nullable(),
  currency: currencyCodeSchema.nullable(),
  templateId: uuidSchema.nullable(),
  templateVersion: z.number().int().nullable(),
  portalVisible: z.boolean(),
  color: z.string().nullable(),
  customFields: z.record(z.string(), z.unknown()),
  tags: z.array(z.string()),
  lastStatusUpdateAt: timestampSchema.nullable(),
  completedAt: timestampSchema.nullable(),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type ProjectDto = z.infer<typeof projectSchema>;

/** Milestone of a project (PRD US-3.1). Ordered by `position`, reordered as a whole list. */
export const milestoneSchema = z.object({
  id: uuidSchema,
  projectId: uuidSchema,
  name: z.string(),
  note: z.string().nullable(),
  dueDate: dateOnlySchema.nullable(),
  status: milestoneStatusSchema,
  position: z.number().int(),
  completedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type MilestoneDto = z.infer<typeof milestoneSchema>;

/** A person assigned to a project, with the allocation Phase 2 workload planning reads. */
export const projectMemberSchema = z.object({
  id: uuidSchema,
  projectId: uuidSchema,
  userId: uuidSchema,
  user: userRefSchema.nullable(),
  roleInProject: z.string().nullable(),
  allocationPercent: z.number().int().nullable(),
  ...timestampsShape,
});
export type ProjectMemberDto = z.infer<typeof projectMemberSchema>;

/** One weekly status post by the PM (PRD US-3.6). Append-only. */
export const projectStatusUpdateSchema = z.object({
  id: uuidSchema,
  projectId: uuidSchema,
  pmId: uuidSchema.nullable(),
  pm: userRefSchema.nullable(),
  status: projectHealthSchema,
  note: z.string().nullable(),
  period: weekKeySchema,
  createdAt: timestampSchema,
});
export type ProjectStatusUpdateDto = z.infer<typeof projectStatusUpdateSchema>;

// ---------------------------------------------------------------------------
// Deliverable quotas and the ledger (PRD US-3.5)
// ---------------------------------------------------------------------------

/** What an engagement commits to per period, e.g. 12 static posts a month with 2 carried forward. */
export const deliverableQuotaConfigSchema = z.object({
  id: uuidSchema,
  engagementId: uuidSchema,
  deliverableType: z.string(),
  committedQty: z.number().int(),
  periodUnit: quotaPeriodUnitSchema,
  rolloverPolicy: rolloverPolicySchema,
  /** Maximum units that may carry into the next period; `null` = uncapped. */
  rolloverCap: z.number().int().nullable(),
  startPeriod: periodSchema.nullable(),
  endPeriod: periodSchema.nullable(),
  isActive: z.boolean(),
  notes: z.string().nullable(),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type DeliverableQuotaConfigDto = z.infer<typeof deliverableQuotaConfigSchema>;

/** One delivered (or planned / in-progress) item metered against a quota. */
export const deliverableLedgerEntrySchema = z.object({
  id: uuidSchema,
  engagementId: uuidSchema,
  projectId: uuidSchema.nullable(),
  quotaId: uuidSchema.nullable(),
  deliverableType: z.string(),
  period: periodSchema,
  qty: z.number().int(),
  state: deliverableStateSchema,
  /** `task`, `approval`, `manual`… — with `sourceId` it makes the entry idempotent. */
  sourceType: z.string(),
  sourceId: uuidSchema.nullable(),
  deliveredAt: timestampSchema.nullable(),
  note: z.string().nullable(),
  createdBy: userRefSchema.nullable(),
  ...timestampsShape,
});
export type DeliverableLedgerEntryDto = z.infer<typeof deliverableLedgerEntrySchema>;

/** The meter shown on the client and project pages: planned / in progress / delivered vs committed. */
export const quotaMeterSchema = z.object({
  /** `null` when the meter is derived from the engagement's declared quota rather than a configured row. */
  quotaId: uuidSchema.nullable(),
  engagementId: uuidSchema,
  deliverableType: z.string(),
  period: periodSchema,
  committedQty: z.number().int(),
  /** Unused units carried in from earlier periods (0 when the policy is `none`). */
  carriedInQty: z.number().int(),
  /** `committedQty + carriedInQty` — what this period actually owes. */
  effectiveCommittedQty: z.number().int(),
  plannedQty: z.number().int(),
  inProgressQty: z.number().int(),
  deliveredQty: z.number().int(),
  remainingQty: z.number().int(),
  carryOutQty: z.number().int(),
  deliveredBps: z.number().int(),
  elapsedBps: z.number().int(),
  atRisk: z.boolean(),
  /** Scope creep: more delivered than committed (PRD US-3.5). */
  overDelivered: z.boolean(),
  rolloverPolicy: rolloverPolicySchema,
  rolloverCap: z.number().int().nullable(),
});
export type QuotaMeterDto = z.infer<typeof quotaMeterSchema>;

export const quotaMeterListSchema = z.object({
  engagementId: uuidSchema,
  period: periodSchema,
  meters: z.array(quotaMeterSchema),
});
export type QuotaMeterListDto = z.infer<typeof quotaMeterListSchema>;

// ---------------------------------------------------------------------------
// Counts, portfolio and the project detail page
// ---------------------------------------------------------------------------

/**
 * Roll-up counters for one project. The milestone half is owned here; the task
 * half is filled by the `tasks` module through the registered counts provider
 * (until it ships, task counters are 0 — see `registerTaskCountsProvider`).
 */
export const projectCountsSchema = z.object({
  taskTotal: z.number().int(),
  taskOpen: z.number().int(),
  taskDone: z.number().int(),
  taskOverdue: z.number().int(),
  milestoneTotal: z.number().int(),
  milestoneDone: z.number().int(),
  /** 0–100, from tasks when they exist, else from milestones, else status. */
  percentComplete: z.number().int(),
});
export type ProjectCountsDto = z.infer<typeof projectCountsSchema>;

/** `GET /projects/:id` — the project with everything its page needs in one round trip. */
export const projectDetailSchema = projectSchema.extend({
  milestones: z.array(milestoneSchema),
  members: z.array(projectMemberSchema),
  quotaMeters: z.array(quotaMeterSchema),
  counts: projectCountsSchema,
  latestStatusUpdate: projectStatusUpdateSchema.nullable(),
});
export type ProjectDetailDto = z.infer<typeof projectDetailSchema>;

/** One row of the portfolio view of US-3.6: status, due date, % complete, overdue tasks. */
export const portfolioRowSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  name: z.string(),
  type: projectTypeSchema,
  status: projectStatusSchema,
  health: projectHealthSchema,
  client: clientRefSchema.nullable(),
  pm: userRefSchema.nullable(),
  startDate: dateOnlySchema.nullable(),
  endDate: dateOnlySchema.nullable(),
  percentComplete: z.number().int(),
  counts: projectCountsSchema,
  quotaAtRisk: z.boolean(),
  lastStatusUpdateAt: timestampSchema.nullable(),
  /** True once the end date has passed and the project is neither completed nor cancelled. */
  overdue: z.boolean(),
});
export type PortfolioRowDto = z.infer<typeof portfolioRowSchema>;

// ---------------------------------------------------------------------------
// Project inputs
// ---------------------------------------------------------------------------

/** Shape WITHOUT defaults, so `.partial()` below cannot resurrect them (zod 4). */
const projectWritableShape = {
  name: z.string().trim().min(1).max(200),
  clientId: uuidSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  type: projectTypeSchema,
  status: projectStatusSchema,
  pmId: uuidSchema.nullable(),
  startDate: dateOnlySchema.nullable(),
  endDate: dateOnlySchema.nullable(),
  description: blankToNull(z.string().max(5000)),
  brief: blankToNull(z.string().max(20000)),
  budgetMinor: minorSchema.min(0).nullable(),
  currency: currencyCodeSchema.nullable(),
  templateId: uuidSchema.nullable(),
  templateVersion: z.number().int().min(1).nullable(),
  portalVisible: z.boolean(),
  color: colorSchema.nullable(),
  customFields: z.record(z.string(), z.unknown()),
  tags: tagsSchema,
};

const memberInputSchema = z.object({
  userId: uuidSchema,
  roleInProject: z.string().trim().max(80).nullable().optional(),
  allocationPercent: z.number().int().min(0).max(100).nullable().optional(),
});

/**
 * POST /api/v1/projects (perm `projects:write`) → 201 Project.
 * A non-internal project without a client → 422; a client from another workspace → 404.
 */
export const createProjectInputSchema = z
  .object({
    ...projectWritableShape,
    clientId: projectWritableShape.clientId.optional(),
    engagementId: projectWritableShape.engagementId.optional(),
    type: projectWritableShape.type.optional(),
    status: projectWritableShape.status.optional(),
    health: projectHealthSchema.optional(),
    pmId: projectWritableShape.pmId.optional(),
    startDate: projectWritableShape.startDate.optional(),
    endDate: projectWritableShape.endDate.optional(),
    description: projectWritableShape.description.optional(),
    brief: projectWritableShape.brief.optional(),
    budgetMinor: projectWritableShape.budgetMinor.optional(),
    currency: projectWritableShape.currency.optional(),
    templateId: projectWritableShape.templateId.optional(),
    templateVersion: projectWritableShape.templateVersion.optional(),
    portalVisible: projectWritableShape.portalVisible.optional(),
    color: projectWritableShape.color.optional(),
    customFields: projectWritableShape.customFields.optional(),
    tags: projectWritableShape.tags.optional(),
    /** Seeds `project_members`; the PM is added automatically. */
    members: z.array(memberInputSchema).max(100).optional(),
  })
  .strict();
export type CreateProjectInput = z.input<typeof createProjectInputSchema>;
export type CreateProjectData = z.output<typeof createProjectInputSchema>;

/** PATCH /api/v1/projects/:id (perm `projects:write`) → Project. Merge patch; omitted keys unchanged. */
export const updateProjectInputSchema = z
  .object({ ...projectWritableShape, health: projectHealthSchema, healthNote: blankToNull(z.string().max(2000)) })
  .partial()
  .strict();
export type UpdateProjectInput = z.input<typeof updateProjectInputSchema>;
export type UpdateProjectData = z.output<typeof updateProjectInputSchema>;

/** GET /api/v1/projects (perm `projects:read`) — a member without `projects:read_all` sees only their own. */
export const listProjectsQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema.optional(),
  engagementId: uuidSchema.optional(),
  status: csvArraySchema(projectStatusSchema, 10).optional(),
  health: csvArraySchema(projectHealthSchema, 5).optional(),
  type: csvArraySchema(projectTypeSchema, 5).optional(),
  pmId: uuidSchema.optional(),
  memberId: uuidSchema.optional(),
  tags: csvArraySchema(z.string().trim().min(1).max(40), 20).optional(),
  /** Overlap window on the project's own start/end dates. */
  startFrom: dateOnlySchema.optional(),
  startTo: dateOnlySchema.optional(),
  dueFrom: dateOnlySchema.optional(),
  dueTo: dateOnlySchema.optional(),
  includeArchived: queryBooleanSchema.optional(),
  portalVisible: queryBooleanSchema.optional(),
  sort: z.enum(["name", "number", "startDate", "endDate", "status", "createdAt"]).optional(),
});
export type ListProjectsQuery = z.infer<typeof listProjectsQuerySchema>;

/** GET /api/v1/projects/portfolio (perm `projects:read`) → the US-3.6 portfolio table. */
export const portfolioQuerySchema = listProjectsQuerySchema.extend({
  /** Only projects whose end date has passed and that are not finished. */
  overdueOnly: queryBooleanSchema.optional(),
  /** Only projects with no status update in the current ISO week. */
  needsStatusUpdate: queryBooleanSchema.optional(),
});
export type PortfolioQuery = z.infer<typeof portfolioQuerySchema>;

/** POST /api/v1/projects/:id/status-update (perm `projects:write`) → 201 status update. */
export const createStatusUpdateInputSchema = z
  .object({
    status: projectHealthSchema,
    note: blankToNull(z.string().max(4000)).optional(),
    period: weekKeySchema.optional(),
  })
  .strict();
export type CreateStatusUpdateInput = z.input<typeof createStatusUpdateInputSchema>;

/** POST /api/v1/projects/:id/health (perm `projects:write`) → Project. */
export const setHealthInputSchema = z
  .object({ health: projectHealthSchema, note: blankToNull(z.string().max(2000)).optional() })
  .strict();
export type SetHealthInput = z.input<typeof setHealthInputSchema>;

// ---------------------------------------------------------------------------
// Milestone inputs
// ---------------------------------------------------------------------------

const milestoneWritableShape = {
  name: z.string().trim().min(1).max(200),
  note: blankToNull(z.string().max(2000)),
  dueDate: dateOnlySchema.nullable(),
  status: milestoneStatusSchema,
  position: z.number().int().min(0).max(10000),
};

/** POST /api/v1/projects/:id/milestones (perm `projects:write`) → 201 Milestone. */
export const createMilestoneInputSchema = z
  .object({
    ...milestoneWritableShape,
    note: milestoneWritableShape.note.optional(),
    dueDate: milestoneWritableShape.dueDate.optional(),
    status: milestoneWritableShape.status.optional(),
    position: milestoneWritableShape.position.optional(),
  })
  .strict();
export type CreateMilestoneInput = z.input<typeof createMilestoneInputSchema>;

/** PATCH /api/v1/milestones/:id (perm `projects:write`) → Milestone. Merge patch. */
export const updateMilestoneInputSchema = z.object(milestoneWritableShape).partial().strict();
export type UpdateMilestoneInput = z.input<typeof updateMilestoneInputSchema>;

/** POST /api/v1/projects/:id/milestones/reorder (perm `projects:write`) → the reordered list. */
export const reorderMilestonesInputSchema = z
  .object({ milestoneIds: z.array(uuidSchema).min(1).max(200) })
  .strict()
  .refine((v) => new Set(v.milestoneIds).size === v.milestoneIds.length, { message: "Milestone ids must be unique.", path: ["milestoneIds"] });
export type ReorderMilestonesInput = z.input<typeof reorderMilestonesInputSchema>;

// ---------------------------------------------------------------------------
// Member inputs
// ---------------------------------------------------------------------------

/** POST /api/v1/projects/:id/members (perm `projects:write`) → 201 member. Re-posting updates the row. */
export const addProjectMemberInputSchema = z
  .object({
    userId: uuidSchema,
    roleInProject: z.string().trim().max(80).nullable().optional(),
    allocationPercent: z.number().int().min(0).max(100).nullable().optional(),
  })
  .strict();
export type AddProjectMemberInput = z.input<typeof addProjectMemberInputSchema>;

export const projectMemberParamsSchema = z.object({ id: uuidSchema, userId: uuidSchema });
export type ProjectMemberParams = z.infer<typeof projectMemberParamsSchema>;

// ---------------------------------------------------------------------------
// Quota / ledger inputs
// ---------------------------------------------------------------------------

const quotaWritableShape = {
  deliverableType: z.string().trim().min(1).max(60),
  committedQty: z.number().int().min(0).max(100000),
  periodUnit: quotaPeriodUnitSchema,
  rolloverPolicy: rolloverPolicySchema,
  rolloverCap: z.number().int().min(0).max(100000).nullable(),
  startPeriod: periodSchema.nullable(),
  endPeriod: periodSchema.nullable(),
  isActive: z.boolean(),
  notes: blankToNull(z.string().max(2000)),
};

/**
 * POST /api/v1/engagements/:id/quotas (perm `projects:write`) → 201 quota.
 * A second quota for the same deliverable type on the same engagement → CONFLICT.
 */
export const createQuotaInputSchema = z
  .object({
    ...quotaWritableShape,
    periodUnit: quotaWritableShape.periodUnit.optional(),
    rolloverPolicy: quotaWritableShape.rolloverPolicy.optional(),
    rolloverCap: quotaWritableShape.rolloverCap.optional(),
    startPeriod: quotaWritableShape.startPeriod.optional(),
    endPeriod: quotaWritableShape.endPeriod.optional(),
    isActive: quotaWritableShape.isActive.optional(),
    notes: quotaWritableShape.notes.optional(),
  })
  .strict();
export type CreateQuotaInput = z.input<typeof createQuotaInputSchema>;

/** PATCH /api/v1/deliverable-quotas/:id (perm `projects:write`) → quota. Merge patch. */
export const updateQuotaInputSchema = z.object(quotaWritableShape).partial().strict();
export type UpdateQuotaInput = z.input<typeof updateQuotaInputSchema>;

/** POST /api/v1/engagements/:id/deliverables (perm `projects:write`) → 201 ledger entry. */
export const recordDeliverableInputSchema = z
  .object({
    deliverableType: z.string().trim().min(1).max(60),
    qty: z.number().int().min(1).max(10000).optional(),
    state: deliverableStateSchema.optional(),
    projectId: uuidSchema.nullable().optional(),
    period: periodSchema.optional(),
    deliveredAt: timestampSchema.optional(),
    note: blankToNull(z.string().max(2000)).optional(),
    sourceType: z.string().trim().min(1).max(40).optional(),
    sourceId: uuidSchema.nullable().optional(),
  })
  .strict();
export type RecordDeliverableInput = z.input<typeof recordDeliverableInputSchema>;

/** GET /api/v1/engagements/:id/deliverables (perm `projects:read`). */
export const listDeliverablesQuerySchema = listQuerySchema.extend({
  period: periodSchema.optional(),
  deliverableType: z.string().trim().max(60).optional(),
  state: csvArraySchema(deliverableStateSchema, 3).optional(),
  projectId: uuidSchema.optional(),
});
export type ListDeliverablesQuery = z.infer<typeof listDeliverablesQuerySchema>;

/** GET /api/v1/engagements/:id/quota-meters (perm `projects:read`). Defaults to the current period. */
export const quotaMetersQuerySchema = z.object({ period: periodSchema.optional() });
export type QuotaMetersQuery = z.infer<typeof quotaMetersQuerySchema>;

export const engagementIdParamSchema = z.object({ id: uuidSchema });
